import { encryptJSON, decryptJSON } from './encryption';

/**
 * Encrypted API client
 * Automatically encrypts requests and decrypts responses
 */

/**
 * Encrypted fetch wrapper
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options (headers, method, etc.)
 * @param {object} body - Request body to encrypt (if any)
 * @returns {Promise<object>} - Decrypted response data
 */
export async function encryptedFetch(url, options = {}, body = null) {
    try {
        // Prepare headers
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        // Skip encryption for import endpoint (large file payloads)
        const isImportEndpoint = url.includes('/admin/import');
        
        // Encrypt body if provided (skip for import endpoints)
        let encryptedBody = null;
        if (body !== null && body !== undefined && !isImportEndpoint) {
            encryptedBody = await encryptJSON(body);
        } else if (body !== null && body !== undefined && isImportEndpoint) {
            // For import, send unencrypted (backend will handle it)
            encryptedBody = null;
        }

        // Make request with encrypted or unencrypted body
        let requestBody;
        if (isImportEndpoint && body) {
            // For import, send unencrypted JSON directly
            requestBody = JSON.stringify(body);
        } else if (encryptedBody) {
            // Encrypted request
            requestBody = JSON.stringify({ data: encryptedBody });
        } else {
            requestBody = options.body;
        }

        const response = await fetch(url, {
            ...options,
            headers,
            body: requestBody
        });

        // Handle non-JSON responses (like errors)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const text = await response.text();
            throw new Error(text || `HTTP ${response.status}`);
        }

        const responseData = await response.json();

        // Skip decryption for import endpoint
        if (isImportEndpoint) {
            return responseData;
        }

        // Check if response is encrypted
        if (responseData.data && typeof responseData.data === 'string') {
            try {
                // Decrypt response
                const decrypted = await decryptJSON(responseData.data);
                return decrypted;
            } catch (decryptError) {
                console.error('Decryption error:', decryptError);
                // If decryption fails, it might be a key mismatch
                if (decryptError.message && decryptError.message.includes('base64')) {
                    throw new Error('Failed to decrypt response. Check that ENCRYPTION_KEY matches between frontend and backend.');
                }
                throw new Error('Failed to decrypt response: ' + decryptError.message);
            }
        } else {
            // Unencrypted response (fallback or error)
            return responseData;
        }
    } catch (error) {
        console.error('API request error:', error);
        // Re-throw with more context if it's a decryption error
        if (error.message && error.message.includes('decrypt')) {
            throw error;
        }
        throw error;
    }
}

/**
 * GET request
 */
export async function encryptedGet(url, headers = {}) {
    return encryptedFetch(url, {
        method: 'GET',
        headers
    });
}

/**
 * POST request
 */
export async function encryptedPost(url, body, headers = {}) {
    return encryptedFetch(url, {
        method: 'POST',
        headers
    }, body);
}

/**
 * PUT request
 */
export async function encryptedPut(url, body, headers = {}) {
    return encryptedFetch(url, {
        method: 'PUT',
        headers
    }, body);
}

/**
 * DELETE request
 */
export async function encryptedDelete(url, headers = {}) {
    return encryptedFetch(url, {
        method: 'DELETE',
        headers
    });
}

